import { forwardRef } from 'react';
import type { JSX, ReactNode, SelectHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Kit Parametres : les blocs standard des panneaux de reglages (hub Parametres
 * du shell, modules settings.tsx des apps), au vocabulaire du gabarit v2.2
 * (variante "Preferences" retenue, un seul vocabulaire : le pont .nt du
 * prototype n'existe pas ici, le kit EST le style final).
 *
 * Trois decisions liees :
 *
 * - la COLONNE BORNEE (SettingsPanel, 42rem) : un space-between sur toute la
 *   largeur met le controle a un metre de son libelle ; sur 42rem, libelle et
 *   controle restent une paire et la colonne de controles s'aligne, ce qui
 *   rend la liste balayable d'un seul mouvement ;
 * - le TITRE SOULIGNE (SettingsSection) : 1rem/600 + filet sous le titre,
 *   plus d'overline capitales. Le filet appartient au TITRE, il ne separe
 *   jamais les lignes entre elles : les reglages sont des paragraphes, pas
 *   les rangees d'une grille ;
 * - la LIGNE (SettingsRow) : libelle 0.875rem/600 ink-700, description
 *   0.8125rem ink-500 bornee a 26rem, respiration verticale 0.75rem.
 *
 * Style PLAT, sans Card : aucun fond de bloc, aucune bordure de groupe,
 * aucune ombre. Un groupe se lit parce qu'il respire (2.5rem au-dessus),
 * jamais parce qu'il est encadre. Un panneau typique :
 *
 * <SettingsPanel>
 *   <SettingsSection title="Affichage" description="Preferences locales au poste.">
 *     <SettingsRow label="Densite" hint="Hauteur des lignes de liste">
 *       ...controle...
 *     </SettingsRow>
 *   </SettingsSection>
 * </SettingsPanel>
 */

export interface SettingsPanelProps {
  /** Les sections du panneau (SettingsSection conseille). */
  children: ReactNode;
}

/**
 * Le cadre commun de TOUS les panneaux : le meme padding partout (sinon la
 * mise en page saute quand on change d'onglet), et LA colonne bornee a 42rem.
 * La borne se pose ICI, une seule fois, et nulle part ailleurs : c'est elle
 * qui corrige la distance libelle / controle des grands ecrans.
 */
export function SettingsPanel({ children }: SettingsPanelProps): JSX.Element {
  return <div className="max-w-[42rem] px-5 pb-12 pt-2 font-sans">{children}</div>;
}

export interface SettingsSectionProps {
  /** Titre du groupe, souligne d'un filet. */
  title: string;
  /** Phrase d'intro optionnelle, SOUS le filet : le premier texte du groupe. */
  description?: string;
  /** Les lignes du groupe (SettingsRow conseille). */
  children: ReactNode;
}

/**
 * Groupe de reglages : titre 1rem/600 souligne d'un filet, description
 * optionnelle sous le filet, puis les lignes. Marge genereuse AU-DESSUS,
 * jamais en dessous ; aucun filet entre les lignes.
 */
export function SettingsSection({
  title,
  description,
  children,
}: SettingsSectionProps): JSX.Element {
  return (
    <section className="mt-10 font-sans first:mt-5">
      <h3 className="border-b border-border pb-2 text-base font-semibold leading-[1.3] tracking-[-0.01em] text-ink-900">
        {title}
      </h3>
      {description != null && (
        <p className="mt-2.5 max-w-[34rem] text-pretty text-[0.8125rem] leading-[1.45] text-ink-500">
          {description}
        </p>
      )}
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

export interface SettingsRowProps {
  /** Libelle du reglage. */
  label: string;
  /** Explication optionnelle sous le libelle. */
  hint?: string;
  /**
   * Pastille de qualification contre le libelle ("toujours visible") : elle
   * dit un ETAT du reglage, pas une action. Encre discrete sur gris, jamais
   * de lime.
   */
  badge?: string;
  /** Le controle du reglage (Switch, SettingsSelect...), aligne a droite. */
  children: ReactNode;
}

/**
 * Ligne de reglage : libelle (+ explication) a gauche, le controle a droite.
 * La premiere ligne respire davantage sous le filet du titre (1.125rem) ;
 * entre deux lignes, la seconde se resserre legerement (0.625rem).
 */
export function SettingsRow({ label, hint, badge, children }: SettingsRowProps): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-6 py-3 font-sans first:pt-[1.125rem] [&+&]:pt-2.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-2 text-sm font-semibold leading-[1.4] text-ink-700">
          {label}
          {badge != null && (
            <span className="rounded-xs bg-surface-3 px-1.5 py-[0.0625rem] text-[0.6875rem] font-medium text-ink-500">
              {badge}
            </span>
          )}
        </span>
        {hint != null && (
          <span className="max-w-[26rem] text-pretty text-[0.8125rem] leading-[1.45] text-ink-500">
            {hint}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center">{children}</div>
    </div>
  );
}

export type SettingsSelectProps = SelectHTMLAttributes<HTMLSelectElement>;

/**
 * La liste deroulante du kit : hauteur 2rem, largeur AU CONTENU bornee a
 * 16rem. Dans une colonne de 42rem, une largeur fixe de 16rem prendrait la
 * moitie de la ligne pour afficher deux mots.
 */
export const SettingsSelect = forwardRef<HTMLSelectElement, SettingsSelectProps>(
  function SettingsSelect({ className, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          'h-8 max-w-[16rem] cursor-pointer rounded-sm border border-border bg-surface px-2',
          'font-sans text-[0.8125rem] font-normal text-ink-900',
          'transition-colors hover:border-border-strong',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
          className,
        )}
        {...props}
      />
    );
  },
);
